import { toJS } from 'mobx';
import { applySnapshot, getSnapshot, Instance, SnapshotOut, types } from 'mobx-state-tree';

import {
  ProjectApi,
  TDeleteCommentsResult,
  TDeleteProjectResult,
  TFileDownloadResult,
  TGetCalendarResult,
  TGetProjectHistoryResult,
  TGetProjectResult,
  TGetProjectsResult,
  TPostCommentsResult,
  TPostProjectResult,
  TUpdateProjectResult,
} from '../../services';
import { Calendar, ICalendar } from '../calendar/Calendar';
import { Comment, createComment, IComment } from '../comment/Comment';
import { withEnvironment } from '../extensions/with-environment';
import { withRootStore } from '../extensions/with-root-store';
import {
  createPagination,
  createPaginationForMobile,
  IPagination,
  IPaginationSnapshot,
} from '../pagination/Pagination';
import {
  createProject,
  IProject,
  IProjectFilter,
  IProjectSnapshot,
  Project,
} from '../project/Project';
import {
  FilteringConditions,
  IFilterConditionsSnapshotIn,
  IFilteringConditions,
} from '../project-filter-conditions/ProjectFilterCondition';
import { createProjectHistory, IProjectHistory } from '../project-history/ProjectHistory';

/**
 * # ProjectStore
 *
 * ProjectStore을 설명하세요.
 */
export const ProjectStore = types
  .model('ProjectStore')
  // --------------------------------------------------------------------------
  .props({
    pagination: createPagination(),
    paginationForMobile: createPaginationForMobile(),
    appendedProjects: types.optional(types.array(Project), []), // 모바일에서 보여지는 프로젝트 리스트
    projects: types.optional(types.array(Project), []), // 데스크탑에서 보여지는 프로젝트 리스트
    project: createProject(),
    projectHistory: createProjectHistory(),
    comment: createComment(),
    comments: types.optional(types.array(Comment), []),
    filterByConditions: types.optional(FilteringConditions, {}),
    sortByConditions: types.optional(types.string, 'order'),
    searchByKeyword: types.optional(types.string, ''),
    calendarData: types.optional(types.array(Calendar), []),
  })
  .extend(withRootStore)
  .extend(withEnvironment)
  // eslint-disable-line @typescript-eslint/no-unused-vars
  .views((self) => ({
    get projectList() {
      return toJS(self.projects);
    },
    get appendedProjectList() {
      return toJS(self.appendedProjects);
    },
    get paginationOptions() {
      return toJS(self.pagination);
    },
    get paginationOptionsForMobile() {
      return toJS(self.paginationForMobile);
    },
    get getFilterByConditions() {
      return toJS(self.filterByConditions);
    },
    get getQueryParams(): IProjectFilter {
      const sort = toJS(self.sortByConditions);
      const filteringConditions = toJS(self.filterByConditions);
      const search = toJS(self.searchByKeyword);
      let filterConditionsObj = {};

      Object.keys(filteringConditions)
        /** FilteringConditions 오브젝트의 value값이 undefined인 것 제외 */
        .filter((key) => (filteringConditions as any)[key] !== undefined)
        /** [{ deprtCd: number }, { bzStepCd: number }]을 추상화 */
        .map((key) => ({ [key]: (filteringConditions as any)[key] }))
        .forEach((conditions) => {
          filterConditionsObj = { ...filterConditionsObj, ...conditions };
        });

      return {
        ...filterConditionsObj,
        sortBy: sort,
        text: search,
      };
    },
  }))
  // --------------------------------------------------------------------------
  // MUTATEs - 모델 상태를 변경
  .actions((self) => ({
    /**
     * projects을 교체
     *
     * @param `projects` 새로운 모델의 배열
     */
    setPagination: (pagination: IPaginationSnapshot) => {
      self.pagination = pagination as IPagination;
    },
    resetPagination: () => {
      self.pagination = {
        page: 1,
        size: 100, // Number(localStorage.getItem('pageSize')),
      } as IPagination;
    },
    setPaginationForMobile: (pagination: IPaginationSnapshot) => {
      self.paginationForMobile = pagination as IPagination;
    },
    setProjects: (projects: IProject[]) => {
      self.projects.replace(projects);
    },
    setProjectHistory: (projectHistory: IProjectHistory) => {
      self.projectHistory = projectHistory;
    },
    setComment: (comment: IComment) => {
      self.comment = comment;
    },
    setComments: (comments: IComment[]) => {
      self.comments.replace(comments);
    },
    setAppendProjects: (projects: IProject[]) => {
      const currentAppendedProjects = getSnapshot(self.appendedProjects);
      const newAppendedProjects = [...currentAppendedProjects, ...projects];
      applySnapshot(self.appendedProjects, newAppendedProjects);
    },
    removeProject: (projectSid: number) => {
      const currentAppendedProjects = getSnapshot(self.appendedProjects);
      const removeAndNewAppendedProjects = currentAppendedProjects.filter(
        (project) => project.projectSid !== projectSid,
      );
      applySnapshot(self.appendedProjects, removeAndNewAppendedProjects);
    },
    setFilterByConditions: (conditions: IFilterConditionsSnapshotIn) => {
      self.filterByConditions = conditions as IFilteringConditions;
    },
    setOrderByConditions: (conditions: string) => {
      self.sortByConditions = conditions;
    },
    setSearchByKeyword: (keyword: string) => {
      self.searchByKeyword = keyword;
    },
    resetProjects: () => {
      self.projects.replace([]);
    },
    resetAppendedProjects: () => {
      self.appendedProjects.replace([]);
    },
    setProject: (project: IProjectSnapshot) => {
      self.project = project as IProject;
    },
    resetProject: () => {
      self.project = {} as IProject;
    },
    setCalendarData: (calendarData: ICalendar[]) => {
      self.calendarData.replace(calendarData);
    },
  }))
  // --------------------------------------------------------------------------
  // REQUESTs - 서비스 요청 및 기타 인터페이스 요청
  .actions((self) => ({
    /**
     * 프로젝트 목록 조회
     * @param filter
     */
    gets: async (filter?: IProjectFilter) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TGetProjectsResult = await projectApi.gets(
          // { ...self.pagination, // page: filter?.page || self.pagination.page, size: filter?.size || self.pagination.size },
          self.pagination,
          // self.paginationOptions,
          filter,
        );
        self.rootStore.responseStore.getResponseResult(result);
        self.resetProjects();
        if (result.kind === 'ok') {
          // if (result.projects) {
          //   result.projects.forEach((item: IProjectSnapshot) => {
          //     // item.conference_date =
          //     // moment(item.start).dayOfYear() === moment(item.end).dayOfYear()
          //     //   ? moment(item.start).format('YYYY-MM-DD HH:mm') +
          //     //     ' ~ ' +
          //     //     moment(item.end).format('HH:mm')
          //     //   : moment(item.start).format('YYYY-MM-DD HH:mm') +
          //     //     ' ~ ' +
          //     //     moment(item.end).format('YYYY-MM-DD HH:mm');
          //   });
          // }

          // const sumRow = result.projects.reduce(
          //   (sum, pj) => {
          //     sum.orderAmt += pj.orderAmt || 0;
          //     sum.salesAmt += pj.salesAmt || 0;
          //     sum.purchaseAmt += pj.purchaseAmt || 0;
          //     sum.rmnngSales += pj.rmnngSales || 0;
          //     return sum;
          //   },
          //   { orderAmt: 0, salesAmt: 0, purchaseAmt: 0, rmnngSales: 0 },
          // );
          // self.setProjects([sumRow as IProject, ...result.projects]);
          self.setProjects(result.projects);
          result.pagination && self.setPagination(result.pagination);
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },
    getsForMobile: async (filter?: IProjectFilter) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TGetProjectsResult = await projectApi.gets(self.paginationForMobile, filter);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          self.setAppendProjects(result.projects);
          result.pagination && self.setPaginationForMobile(result.pagination);
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /**
     * 프로젝트 상세 조회
     * @param projectSid
     */
    get: async (projectSid: number) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TGetProjectResult = await projectApi.get(projectSid);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          if (result.project) {
            let classifiedSalesData;
            if (result.project.roundSalesList) {
              classifiedSalesData = result.project.roundSalesList.reduce(
                (roundSales: any, obj: any) => {
                  const code = obj.projectTypeCd.code;
                  const existingItem = roundSales.find((item: any) => item.projectTypeCd === code);
                  if (existingItem) {
                    existingItem.data.push(obj);
                  } else {
                    roundSales.push({ projectTypeCd: code, data: [obj] });
                  }
                  return roundSales;
                },
                [],
              );
            }
            let classifiedPurchaseData;
            if (result.project.purchaseList) {
              classifiedPurchaseData = result.project.purchaseList.reduce(
                (purchase: any, obj: any) => {
                  const code = obj.projectTypeCd.code;
                  const existingItem = purchase.find((item: any) => item.projectTypeCd === code);
                  if (existingItem) {
                    existingItem.data.push(obj);
                  } else {
                    purchase.push({ projectTypeCd: code, data: [obj] });
                  }
                  return purchase;
                },
                [],
              );
            }
            result.project.comments?.reverse();
            self.setProject({
              ...result.project,
              classifiedRoundSales: classifiedSalesData,
              classifiedPurchase: classifiedPurchaseData,
              // comments: result.project.comments?.reverse(),
            });
          }

          // self.setProject({ ...result.project });
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },
    /**
     * 변동이력 조회
     * @param projectSid
     */
    getHistory: async (projectSid: number) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TGetProjectHistoryResult = await projectApi.getHistory(projectSid);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          if (result.projectHistory) {
            self.setProjectHistory(result.projectHistory);
          }
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },
    /**
     * 견적서 파일 다운로드
     * @param projectSid
     */
    fileDownload: async (projectSid: number, filePath: string) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TFileDownloadResult = await projectApi.fileDownload(projectSid, filePath);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          console.log(result);
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    // /**
    //  * 프로젝트 댓글(변동내역) 조회
    //  * @param projectSid
    //  */
    // getComments: async (projectSid: number) => {
    //   try {
    //     const projectApi: ProjectApi = new ProjectApi(self.environment.api);
    //     const result: TGetCommentsResult = await projectApi.getComments(projectSid);
    //     self.rootStore.responseStore.getResponseResult(result);

    //     if (result.kind === 'ok') {
    //       if (result.comments) {
    //         self.setProject({
    //           ...self.project,
    //           comments: result.comments.reverse(), // 댓글 최신순 정렬
    //         });
    //       }
    //     } else {
    //       console.error(result.kind);
    //     }
    //   } catch (e) {
    //     self.rootStore.responseStore.errorProcessing(e);
    //   }
    // },
    /**
     * 프로젝트 댓글(변동내역) 등록/수정
     */
    updateComment: async (projectSid: number, comment: { comment: string; commentSid: number }) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TPostCommentsResult =
          comment.commentSid === 0
            ? await projectApi.postComment(projectSid, comment.comment)
            : await projectApi.putComment(projectSid, comment.commentSid, comment.comment);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          if (result.comment) {
            // self.setProject({
            //   ...self.project,
            //   comments: [result.comment, ...(self.project.comments?.reverse() || [])], // 댓글 최신순 정렬
            // });
            self.setComment(result.comment);
            return;
          }
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /**
     * 프로젝트 댓글(변동내역) 삭제
     */
    deleteComment: async (projectSid: number, commentSid: number) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TDeleteCommentsResult = await projectApi.deleteComment(
          projectSid,
          commentSid,
        );
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          return;
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /**
     * 프로젝트 등록
     */
    post: async (project: IProjectSnapshot) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TPostProjectResult = await projectApi.post(project);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          if (result.project) {
            let classifiedSalesData;
            if (result.project.roundSalesList) {
              classifiedSalesData = result.project.roundSalesList.reduce(
                (roundSales: any, obj: any) => {
                  const code = obj.projectTypeCd.code;
                  const existingItem = roundSales.find((item: any) => item.projectTypeCd === code);
                  if (existingItem) {
                    existingItem.data.push(obj);
                  } else {
                    roundSales.push({ projectTypeCd: code, data: [obj] });
                  }
                  return roundSales;
                },
                [],
              );
            }
            let classifiedPurchaseData;
            if (result.project.purchaseList) {
              classifiedPurchaseData = result.project.purchaseList.reduce(
                (purchase: any, obj: any) => {
                  const code = obj.projectTypeCd.code;
                  const existingItem = purchase.find((item: any) => item.projectTypeCd === code);
                  if (existingItem) {
                    existingItem.data.push(obj);
                  } else {
                    purchase.push({ projectTypeCd: code, data: [obj] });
                  }
                  return purchase;
                },
                [],
              );
            }
            self.setProject({
              ...result.project,
              classifiedRoundSales: classifiedSalesData,
              classifiedPurchase: classifiedPurchaseData,
            });
          }
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /**
     * 프로젝트 수정
     * @param project
     */
    update: async (project: IProjectSnapshot) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TUpdateProjectResult = await projectApi.update(project);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          if (result.project) {
            let classifiedSalesData;
            if (result.project.roundSalesList) {
              classifiedSalesData = result.project.roundSalesList.reduce(
                (roundSales: any, obj: any) => {
                  const code = obj.projectTypeCd.code;
                  const existingItem = roundSales.find((item: any) => item.projectTypeCd === code);
                  if (existingItem) {
                    existingItem.data.push(obj);
                  } else {
                    roundSales.push({ projectTypeCd: code, data: [obj] });
                  }
                  return roundSales;
                },
                [],
              );
            }
            let classifiedPurchaseData;
            if (result.project.purchaseList) {
              classifiedPurchaseData = result.project.purchaseList.reduce(
                (purchase: any, obj: any) => {
                  const code = obj.projectTypeCd.code;
                  const existingItem = purchase.find((item: any) => item.projectTypeCd === code);
                  if (existingItem) {
                    existingItem.data.push(obj);
                  } else {
                    purchase.push({ projectTypeCd: code, data: [obj] });
                  }
                  return purchase;
                },
                [],
              );
            }
            result.project.comments?.reverse();
            self.setProject({
              ...result.project,
              classifiedRoundSales: classifiedSalesData,
              classifiedPurchase: classifiedPurchaseData,
            });
          }
        } else {
          console.error(result);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /**
     * 프로젝트 삭제
     * @param projectSid
     */
    delete: async (projectSid: number) => {
      try {
        const conferenceApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TDeleteProjectResult = await conferenceApi.delete(projectSid);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          return;
        } else {
          console.error(result);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },

    /**
     * 캘린더 조회
     * @param filter
     */
    calendar: async (filter?: IProjectFilter) => {
      try {
        const projectApi: ProjectApi = new ProjectApi(self.environment.api);
        const result: TGetCalendarResult = await projectApi.calendar(filter);
        self.rootStore.responseStore.getResponseResult(result);

        if (result.kind === 'ok') {
          self.setCalendarData(result.data);
        } else {
          console.error(result.kind);
        }
      } catch (e) {
        self.rootStore.responseStore.errorProcessing(e);
      }
    },
  }));

// --------------------------------------------------------------------------
type TProjectStore = Instance<typeof ProjectStore>;
type TProjectStoreSnapshot = SnapshotOut<typeof ProjectStore>;

export interface IProjectStore extends TProjectStore {}
export type TProjectStoreKeys = keyof TProjectStoreSnapshot & string;
export interface IProjectStoreSnapshot extends TProjectStoreSnapshot {}
export const createProjectStore = () => types.optional(ProjectStore, {} as TProjectStore);
